// CACHE BUST: Updated to position menu above button
import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Button } from 'react-bootstrap'
import { MoreHorizontal, Eye, Edit, Trash2, Copy, AlertTriangle, Clock, CheckCircle, XCircle } from 'lucide-react'
import classNames from 'classnames'

const ActionMenu = ({
  actions = [],
  onAction,
  size = 'sm',
  variant = 'outline-secondary',
  className = '',
  disabled = false,
  ...props
}) => {
  console.log('ActionMenu: Component loaded!')
  const [show, setShow] = useState(false)
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 })
  const buttonRef = useRef(null)
  const menuRef = useRef(null)

  // Calculate menu position when showing
  useEffect(() => {
    if (show && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      console.log('ActionMenu: Positioning menu above button at:', rect.top - 80)
      setMenuPosition({
        top: rect.top - 80, // Position above the button
        left: rect.right - 160 // Align to right edge
      })
    }
  }, [show])

  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) && 
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setShow(false)
      }
    }

    if (show) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [show])

  // Handle toggle
  const handleToggle = () => {
    console.log('ActionMenu: Button clicked!', show)
    setShow(!show)
  }

  // Handle action click
  const handleActionClick = (action) => {
    onAction?.(action)
    setShow(false)
  }

  // Get default action icons
  const getActionIcon = (actionType) => {
    const iconMap = {
      view: <Eye size={16} />,
      edit: <Edit size={16} />,
      delete: <Trash2 size={16} />,
      copy: <Copy size={16} />,
      risk: <AlertTriangle size={16} />,
      history: <Clock size={16} />,
      approve: <CheckCircle size={16} />,
      cancel: <XCircle size={16} />
    }
    return iconMap[actionType] || null
  }

  // Filter enabled actions
  const enabledActions = actions.filter(action => !action.disabled)

  if (enabledActions.length === 0) {
    return null
  }

  const menuContent = show && (
    <div 
      ref={menuRef}
      className="action-menu-dropdown-portal"
      style={{
        position: 'fixed',
        top: menuPosition.top,
        left: menuPosition.left,
        zIndex: 99999,
        background: 'white',
        border: '1px solid #e9ecef',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        minWidth: '160px',
        padding: '4px 0'
      }}
    >
      {enabledActions.map((action, index) => (
        <button
          key={index}
          className={classNames('action-menu-item', {
            'text-danger': action.type === 'delete',
            'text-warning': action.type === 'risk'
          })}
          onClick={() => handleActionClick(action)}
          disabled={action.disabled}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: '100%',
            padding: '8px 16px',
            background: 'none',
            border: 'none',
            color: '#495057',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            textAlign: 'left'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = action.type === 'delete' ? '#fee2e2' : 
                                          action.type === 'risk' ? '#fef3c7' : '#f8f9fa'
            e.target.style.color = action.type === 'delete' ? '#dc2626' : 
                                 action.type === 'risk' ? '#d97706' : '#1B1F3B'
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent'
            e.target.style.color = '#495057'
          }}
        >
          <span style={{ color: '#6A4CA2', flexShrink: 0 }}>
            {action.icon || getActionIcon(action.type)}
          </span>
          {action.label}
        </button>
      ))}
    </div>
  )

  return (
    <div className="action-menu-container">
      <Button
        ref={buttonRef}
        variant={variant}
        size={size}
        className={classNames('action-menu-btn', className)}
        onClick={handleToggle}
        disabled={disabled}
        {...props}
      >
        <MoreHorizontal size={16} />
      </Button>

      {show && createPortal(menuContent, document.body)}
    </div>
  )
}

export default ActionMenu